import {useMemo} from 'react'
import {useInfiniteQuery} from '@tanstack/react-query'
import {useAuthorizationHeader} from '@salesforce/commerce-sdk-react/hooks/useAuthorizationHeader'
import useConfig from '@salesforce/commerce-sdk-react/hooks/useConfig'
import {useQuery} from '@salesforce/commerce-sdk-react/hooks/useQuery'
import {
    mergeOptions,
    omitNullableParameters,
    pickValidParams
} from '@salesforce/commerce-sdk-react/hooks/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {ShopperExperience} from 'commerce-sdk-isomorphic'
import {keysToCamel} from '../commerce-api/utils'
import useMultiSite from './use-multi-site'

const useShopperExperienceClient = () => {
    const config = useConfig()

    return useMemo(
        () =>
            new ShopperExperience({
                proxy: config.proxy,
                headers: config.headers,
                parameters: {
                    clientId: config.clientId,
                    organizationId: config.organizationId,
                    shortCode: config.shortCode,
                    siteId: config.siteId,
                    locale: config.locale,
                    currency: config.currency
                },
                throwOnBadResponse: true,
                fetchOptions: config.fetchOptions
            }),
        [config]
    )
}

const getNextContentSearchOffset = (lastPage) => {
    if (lastPage?.next) {
        const url = new URL(lastPage.next, getConfig().forceAppOrigin || 'https://www.stokke.com')
        const offset = url.searchParams.get('offset') || url.searchParams.get('start')
        return offset ? Number(offset) : undefined
    }

    const offset = lastPage?.offset ?? 0
    const limit = lastPage?.limit ?? 0
    const total = lastPage?.total ?? 0
    const nextOffset = offset + limit

    return nextOffset < total ? nextOffset : undefined
}

/**
 * Fetches content assets through the standard SCAPI Shopper Experience getContent endpoint.
 *
 * @example
 * const {data} = useShopperContentAsset({parameters: {cid: 'pwa_footer'}})
 *
 * @param {{parameters: {cid: string}}} apiOptions
 * @param {import("@tanstack/react-query").UseQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseQueryResult}
 */
export const useShopperContentAsset = (apiOptions = {}, queryOptions) => {
    const {cid, ...rest} = apiOptions.parameters || {}
    const {locale, site} = useMultiSite()
    const client = useShopperExperienceClient()
    const methodName = 'getContent'
    const requiredParameters = ShopperExperience.paramKeys[`${methodName}Required`]

    const netOptions = omitNullableParameters(
        mergeOptions(client, {
            ...apiOptions,
            parameters: {
                id: cid,
                siteId: site.id,
                locale: locale.id,
                ...rest
            }
        })
    )
    const parameters = pickValidParams(
        netOptions.parameters,
        ShopperExperience.paramKeys[methodName]
    )
    const queryKey = [
        '/commerce-sdk-react',
        '/organizations/',
        netOptions.parameters.organizationId,
        '/contents/',
        cid,
        parameters
    ]
    const method = async (options) => await client[methodName](options)

    return useQuery(
        {
            ...netOptions,
            parameters
        },
        {
            ...getConfig().app.queryCache?.static,
            ...queryOptions,
            select: (data) => {
                const normalizedData = keysToCamel(data)
                return queryOptions?.select ? queryOptions.select(normalizedData) : normalizedData
            },
            enabled: Boolean(cid && cid !== '-') && queryOptions?.enabled !== false
        },
        {
            method,
            queryKey,
            requiredParameters
        }
    )
}

/**
 * Searches content assets through the standard SCAPI Shopper Experience searchContent endpoint.
 *
 * @example
 * const {data, fetchNextPage} = useShopperContentSearch({
 *     parameters: {refine: 'fdid=stokke-stories', limit: 3}
 * })
 *
 * @param {{parameters: {q?: string, refine?: string, sort?: string, limit?: number, offset?: number}}} apiOptions
 * @param {import("@tanstack/react-query").UseInfiniteQueryOptions} [queryOptions]
 * @returns {import("@tanstack/react-query").UseInfiniteQueryResult}
 */
export const useShopperContentSearch = (apiOptions = {}, queryOptions) => {
    const {locale, site} = useMultiSite()
    const client = useShopperExperienceClient()
    const methodName = 'searchContent'
    const requiredParameters = ShopperExperience.paramKeys[`${methodName}Required`]
    const netOptions = omitNullableParameters(
        mergeOptions(client, {
            ...apiOptions,
            parameters: {
                siteId: site.id,
                locale: locale.id,
                ...apiOptions.parameters
            }
        })
    )
    const parameters = pickValidParams(
        netOptions.parameters,
        ShopperExperience.paramKeys[methodName]
    )
    const method = useAuthorizationHeader(async (options) => await client[methodName](options))

    return useInfiniteQuery({
        queryKey: [
            '/commerce-sdk-react',
            '/organizations/',
            netOptions.parameters.organizationId,
            '/content-search',
            parameters
        ],
        queryFn: ({pageParam}) =>
            method({
                ...netOptions,
                parameters: omitNullableParameters({
                    parameters: {
                        ...parameters,
                        offset: pageParam ?? parameters.offset
                    }
                }).parameters
            }).then(keysToCamel),
        getNextPageParam: getNextContentSearchOffset,
        ...getConfig().app.queryCache?.static,
        ...queryOptions,
        enabled:
            requiredParameters.every((parameter) => parameters[parameter] != null) &&
            queryOptions?.enabled !== false
    })
}
